import React, { useCallback, useMemo, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import styled from 'styled-components';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { useBlockDismissTimer } from '@/features/ai-agents/chat/block-status-utils';
import {
  BlockStatusList,
  BlockStatusRow,
  ExpandCode,
  ExpandContent,
  ExpandPre,
} from '@/features/ai-agents/chat/block-status-row';
import { capitalize } from '@/utils/types';

export interface CodeGenerationStatusProps {
  markdown: string;
  isLatestMessage: boolean;
  isStreamingMessage: boolean;
}

interface ExtractedCodeBlock {
  language: CodeLanguage;
  code: string;
  completed: boolean;
}

const CODE_LANGUAGES = [`html`, `css`, `javascript`] as const;

type CodeLanguage = typeof CODE_LANGUAGES[number];

const CODE_FENCE = String.fromCharCode(96).repeat(3);

const CODE_LABELS: Record<CodeLanguage, string> = {
  html: `Layout & Structure`,
  css: `Styles`,
  javascript: `Logic`,
};

const escapeForRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`);

const hasStartedBlock = (markdown: string, language: CodeLanguage): boolean => {
  const pattern = new RegExp(
    `${CODE_FENCE}\\s*${escapeForRegex(language)}\\b`,
    `i`,
  );
  return pattern.test(markdown);
};

const hasCompletedBlock = (
  markdown: string,
  language: CodeLanguage,
): boolean => {
  const pattern = new RegExp(
    `${CODE_FENCE}\\s*${escapeForRegex(language)}\\b[\\s\\S]*?${CODE_FENCE}`,
    `i`,
  );
  return pattern.test(markdown);
};

const getCodeBlockProgress = (
  markdown: string,
): Record<CodeLanguage, { started: boolean; completed: boolean }> => ({
  html: {
    started: hasStartedBlock(markdown, `html`),
    completed: hasCompletedBlock(markdown, `html`),
  },
  css: {
    started: hasStartedBlock(markdown, `css`),
    completed: hasCompletedBlock(markdown, `css`),
  },
  javascript: {
    started: hasStartedBlock(markdown, `javascript`),
    completed: hasCompletedBlock(markdown, `javascript`),
  },
});

const extractGeneratedCodeBlock = (
  markdown: string,
  language: CodeLanguage,
): ExtractedCodeBlock | null => {
  const pattern = new RegExp(
    `${CODE_FENCE}\\s*${escapeForRegex(
      language,
    )}\\b[^\\n]*\\n?([\\s\\S]*?)(?:${CODE_FENCE}|$)`,
    `i`,
  );
  const match = markdown.match(pattern);

  if (!match) return null;

  const code = (match[1] ?? ``).trimEnd();

  if (!code.length) return null;

  return { language, code, completed: hasCompletedBlock(markdown, language) };
};

export interface CollapsibleCodeBlockProps {
  code: string;
  language?: string;
  isGenerating?: boolean;
  width?: string;
}

const COPY_RESET_DELAY_MS = 2000;

const CodeBlockWrapper = styled.div`
  position: relative;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  border: 1px solid rgba(17, 17, 17, 0.12);
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.9);
  color: #4c5560;
  font-size: 1.4rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #111;
  }
`;

export const CollapsibleCodeBlock = ({
  code,
  isGenerating,
  language,
  width,
}: CollapsibleCodeBlockProps) => {
  const lang = language.replace(`language-`, ``);
  const codeLabel = CODE_LABELS[lang] || capitalize(lang);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
    });
  }, [code]);

  return (
    <BlockStatusRow
      label={
        isGenerating ? `Generating ${codeLabel}...` : `${codeLabel} generated`
      }
      width={width}
      completed={!isGenerating}
      expandable={!!code.length}
      expanded={expanded}
      onToggleExpand={() => setExpanded((prev) => !prev)}
    >
      <ExpandContent
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: `auto` }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2, ease: `easeOut` }}
      >
        <CodeBlockWrapper>
          <CopyButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard();
            }}
          >
            {copied ? <MdCheck /> : <MdContentCopy />}
          </CopyButton>
          <ExpandPre>
            <ExpandCode>{code}</ExpandCode>
          </ExpandPre>
        </CodeBlockWrapper>
      </ExpandContent>
    </BlockStatusRow>
  );
};

interface CodeStatusItemProps {
  language: CodeLanguage;
  started: boolean;
  completed: boolean;
  code: string;
  isLatestMessage: boolean;
  isStreamingMessage: boolean;
}

export const CodeStatusItem = ({
  language,
  started,
  completed,
  code,
  isLatestMessage,
  isStreamingMessage,
}: CodeStatusItemProps) => {
  const [expanded, setExpanded] = useState(false);

  const dismissed = useBlockDismissTimer(
    isLatestMessage && started && completed,
    !isLatestMessage || (!started && !isStreamingMessage),
  );

  if (dismissed) return null;

  return (
    <BlockStatusRow
      label={
        completed
          ? `${CODE_LABELS[language]} generated`
          : `Generating ${CODE_LABELS[language]}...`
      }
      completed={completed}
      expandable
      expanded={expanded}
      onToggleExpand={() => setExpanded((prev) => !prev)}
    >
      <ExpandContent
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: `auto` }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2, ease: `easeOut` }}
      >
        <ExpandPre>
          <ExpandCode>{code}</ExpandCode>
        </ExpandPre>
      </ExpandContent>
    </BlockStatusRow>
  );
};

export const CodeGenerationStatus = ({
  markdown,
  isLatestMessage,
  isStreamingMessage,
}: CodeGenerationStatusProps) => {
  const progress = useMemo(() => getCodeBlockProgress(markdown), [markdown]);

  const hasAnyStarted = CODE_LANGUAGES.some((l) => progress[l].started);
  if (!hasAnyStarted) return null;

  if (
    progress.html.completed &&
    progress.css.completed &&
    progress.javascript.completed
  ) {
    return null;
  }

  return (
    <BlockStatusList>
      <AnimatePresence>
        {CODE_LANGUAGES.map((language) => (
          <CodeStatusItem
            key={language}
            language={language}
            started={progress[language].started}
            completed={progress[language].completed}
            code={extractGeneratedCodeBlock(markdown, language)?.code ?? ``}
            isLatestMessage={isLatestMessage}
            isStreamingMessage={isStreamingMessage}
          />
        ))}
      </AnimatePresence>
    </BlockStatusList>
  );
};
